import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { StarRating, StarDisplay } from '@/components/common/StarRating';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import {
  useGetReviewsQuery,
  useGetMyReviewQuery,
  useSubmitReviewMutation,
  useDeleteMyReviewMutation,
} from '@/redux/review/review.api.slice';

function formatDate(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ReviewsModal: two-tab modal (read list + write/edit/delete form) with auth
// gating; inherently branchy presentation logic, not reducible.
// fallow-ignore-next-line complexity
export default function ReviewsModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(selectCurrentUser);

  const { data, isLoading } = useGetReviewsQuery(undefined, {
    skip: !open,
  });
  const { data: myReview } = useGetMyReviewQuery(undefined, {
    skip: !open || !isAuthenticated,
  });
  const [submitReview, { isLoading: isSubmitting }] = useSubmitReviewMutation();
  const [deleteMyReview, { isLoading: isDeleting }] =
    useDeleteMyReviewMutation();

  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');

  const handleDelete = async () => {
    try {
      await deleteMyReview().unwrap();
      setRating(0);
      setText('');
      toast.success('Your review was deleted');
    } catch (error) {
      const description =
        (error as { data?: { message?: string } })?.data?.message ??
        'Please try again in a moment.';
      toast.error('Could not delete review', { description });
    }
  };

  // Prefill the write tab with the user's existing review (editing re-submits).
  useEffect(() => {
    if (myReview) {
      setRating(myReview.rating);
      setText(myReview.text);
    }
  }, [myReview]);

  // Submit guard branches + try/catch error mapping; inherently branchy.
  // fallow-ignore-next-line complexity
  const handleSubmit = async () => {
    if (rating < 1) {
      toast.error('Please pick a star rating');
      return;
    }
    if (!text.trim()) {
      toast.error('Please write a short review');
      return;
    }
    try {
      await submitReview({ rating, text: text.trim() }).unwrap();
      toast.success('Thanks for your review!', {
        description: 'Your review is now live.',
      });
      onOpenChange(false);
    } catch (error) {
      const description =
        (error as { data?: { message?: string } })?.data?.message ??
        'Please try again in a moment.';
      toast.error('Could not submit review', { description });
    }
  };

  const reviews = data?.reviews ?? [];

  let submitLabel = 'Submit review';
  if (isSubmitting) {
    submitLabel = 'Submitting…';
  } else if (myReview) {
    submitLabel = 'Update review';
  }

  const renderReadBody = () => {
    if (isLoading) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center">
          Loading reviews…
        </p>
      );
    }
    if (reviews.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center">
          No reviews yet. Be the first to leave one!
        </p>
      );
    }
    return (
      <ul className="space-y-3">
        {reviews.map((r) => (
          <li key={r._id} className="rounded-lg border border-[#D6D6D6] p-3">
            <div className="flex items-center gap-2 mb-1.5">
              {r.authorAvatarUrl ? (
                <img
                  src={r.authorAvatarUrl}
                  alt={r.authorName}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                  {r.authorName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{r.authorName}</p>
              </div>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatDate(r.createdAt)}
              </span>
            </div>
            <StarDisplay rating={r.rating} size="sm" />
            <p className="text-sm mt-1.5 whitespace-pre-wrap">{r.text}</p>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Reviews</DialogTitle>
          <DialogDescription>
            See what the community thinks of Filefinity, or share your own.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="read" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="read">Read reviews</TabsTrigger>
            <TabsTrigger value="write">
              {myReview ? 'Edit your review' : 'Write a review'}
            </TabsTrigger>
          </TabsList>

          {/* READ TAB */}
          <TabsContent
            value="read"
            className="flex-1 overflow-y-auto min-h-0 mt-3"
          >
            {data && data.total > 0 && (
              <div className="flex items-center gap-3 pb-3 border-b mb-3">
                <span className="text-2xl font-semibold">
                  {data.averageRating.toFixed(1)}
                </span>
                <div>
                  <StarDisplay rating={data.averageRating} size="sm" />
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {data.total} review{data.total === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            )}

            {renderReadBody()}
          </TabsContent>

          {/* WRITE TAB */}
          <TabsContent
            value="write"
            className="flex-1 overflow-y-auto min-h-0 mt-3"
          >
            {!isAuthenticated ? (
              <div className="text-center py-10 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Sign in to leave a review.
                </p>
                <Button
                  onClick={() => {
                    onOpenChange(false);
                    dispatch(setShowLoginDialog(true));
                  }}
                >
                  Sign in
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {myReview && (
                  <p className="text-xs rounded-md bg-muted text-muted-foreground border px-3 py-2">
                    You&apos;ve already reviewed Filefinity. Editing will update
                    your review.
                  </p>
                )}
                <StarRating
                  rating={rating}
                  onRatingChange={setRating}
                  label="Your rating"
                  size="lg"
                />
                <div className="space-y-1.5">
                  <Label htmlFor="review-text">Your review</Label>
                  <Textarea
                    id="review-text"
                    value={text}
                    rows={5}
                    maxLength={2000}
                    placeholder="What do you think of Filefinity?"
                    onChange={(e) => setText(e.target.value)}
                  />
                </div>
                <div className="flex justify-between items-center">
                  {myReview ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          disabled={isDeleting}
                        >
                          Delete my review
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Delete your review?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes your review. You can write
                            a new one later.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={handleDelete}>
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <span />
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || rating < 1 || !text.trim()}
                  >
                    {submitLabel}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
